import type { RolesTypes } from "@hub/common-libs";
import environment from "environment";
import { default as i18n } from "i18next";
import { GetState, SetState } from "zustand";

import { BaseService } from "@services/BaseService";

import { CombinedStore, RoleManagementStore } from "../useCombinedStore.types";

export const roleManagementStore = (
  set: SetState<CombinedStore>,
  get: GetState<CombinedStore>
): RoleManagementStore => ({
  roles: [],
  role: {
    roleId: "",
    roleFriendlyName: "",
    permissionMatrix: {},
    description: "",
  },
  getPermissions: {
    canAdd: false,
    canEdit: false,
    canView: false,
    canDelete: false,
  },
  roleValidationSchema: [],
  rolePaginationKey: "",
  getRoles: {
    roleActions: null,
    loading: false,
    fetched: false,
    error: "",
    call: (includeProducts, ignoreState) => {
      includeProducts = includeProducts || false;
      ignoreState = ignoreState || false;

      if (!ignoreState) {
        set((state) => {
          state.getRoles.loading = true;
          state.getRoles.error = "";
        });
      }
      const baseUrl =
        get().rolePaginationKey === ""
          ? environment.api.roles +
            "?" +
            i18n.t("shared:users:include_products", {
              include: includeProducts,
            })
          : environment.api.roles +
            "?" +
            i18n.t("shared:users:include_products", {
              include: includeProducts,
            }) +
            "&paginationKey=" +
            get().rolePaginationKey;

      return new Promise<void>((resolve, reject) => {
        BaseService.get(baseUrl)
          .then((res) => {
            if (!ignoreState) {
              set((state) => {
                state.roles = [];
              });
            }

            const data = res.data.data;
            const roles = data.roles;
            if (data && data.roles && data.roles.length > 0) {
              // If the `ignoreState` flag is FALSE, then set the state
              if (!ignoreState) {
                set((state) => {
                  state.roles = roles;
                  state.getRoles.fetched = true;
                  state.rolePaginationKey =
                    data.paginationKey !== undefined ? data.paginationKey : "";
                });
              }
            } else {
              set((state) => {
                state.rolePaginationKey = "";
              });
            }

            if (data && data.actions) {
              const actions = data.actions;
              // Add actions to the 'actions' state.
              set((state) => {
                state.getRoles.roleActions = actions;
              });

              set((state) => {
                state.getPermissions.canAdd = actions.hasOwnProperty("add");
                state.getPermissions.canEdit = actions.hasOwnProperty("edit");
                state.getPermissions.canView = actions.hasOwnProperty("view");
                state.getPermissions.canDelete =
                  actions.hasOwnProperty("delete");
              });
            }
            resolve(roles);
          })
          .catch((error) => {
            set((state) => {
              state.getRoles.error = i18n.t(
                "common:roles:failed_to_load_roles"
              );
            });
            reject(error);
          })
          .finally(() => {
            // If the `ignoreState` flag is FALSE, then set the state
            if (!ignoreState) {
              set((state) => {
                state.getRoles.loading = false;
                state.getRoles.fetched = true;
              });
            }
          });
      });
    },
  },
  fetchRoleValidationSchema: {
    loading: false,
    error: "",
    call: () => {
      set((state) => {
        state.fetchRoleValidationSchema.loading = true;
      });
      return new Promise<void>((resolve, reject) => {
        BaseService.get(environment.api.roleSchema)
          .then((res) => {
            set((state) => {
              state.roleValidationSchema = res.data;
            });
            resolve();
          })
          .catch(() => {
            set((state) => {
              state.fetchRoleValidationSchema.error = i18n.t(
                "common:roles:api_failure"
              );
            });
            reject();
          })
          .finally(() => {
            set((state) => {
              state.fetchRoleValidationSchema.loading = false;
            });
          });
      });
    },
  },
  addRole: {
    loading: false,

    call: (role: RolesTypes.RoleModel) => {
      const roleIdString = role.roleId;
      role.roleId = roleIdString?.split("$")[0];
      set((state) => {
        state.addRole.loading = true;
      });
      return new Promise<RolesTypes.GetRoleResponse>((resolve, reject) => {
        BaseService.post(environment.api.roles, role)
          .then((res) => {
            set(() => {
              get().getRoles.call();
            });
            resolve(res.data);
          })
          .catch((error) => {
            reject(error.response);
          })
          .finally(() => {
            set((state) => {
              state.addRole.loading = false;
            });
          });
      });
    },
  },
  updateRole: {
    loading: false,
    call: (role: RolesTypes.RoleModel) => {
      const roleId = role.roleId;
      let roleIdString = "";

      if (role.roleId && role.roleId !== "") {
        roleIdString = role.roleId;
        role.roleId = roleIdString?.split("$")[0];
      }

      const roleDetails: Partial<RolesTypes.RoleModel> = role;
      delete roleDetails.roleId;

      set((state) => {
        state.addRole.loading = true;
      });
      return new Promise<RolesTypes.UpdateRoleResponse>((resolve, reject) => {
        BaseService.put(environment.api.roles + "/" + roleId, roleDetails)
          .then((res) => {
            set(() => {
              role.roleId = encodeURI(roleId);
              role.roleFriendlyName = roleIdString.split("$")[1];
            });
            resolve(res.data);
          })
          .catch((error) => {
            reject(error.response);
          })
          .finally(() => {
            set((state) => {
              state.addRole.loading = false;
            });
          });
      });
    },
  },
  fetchRole: {
    loading: false,
    fetched: false,
    error: "",
    roleActions: null,
    call: (roleId) => {
      set((state) => {
        state.fetchRole.loading = true;
        state.fetchRole.fetched = false;
        state.fetchRole.error = "";
      });
      const res = BaseService.get(environment.api.roles + "/" + roleId)
        .then((res) => {
          set((state) => {
            state.role = res.data.data.role;
            state.fetchRole.fetched = true;
          });
          const data = res.data.data;
          if (data && data.actions) {
            set((state) => {
              state.fetchRole.roleActions = data.actions;
            });
          }
          return res;
        })
        .catch((error) => {
          set((state) => {
            state.fetchRole.error =
              error.response?.data?.message ||
              i18n.t("core:roles:fetch_role_error");
          });
          return error;
        })
        .finally(() => {
          set((state) => {
            state.fetchRole.loading = false;
            state.fetchRole.fetched = true;
          });
        });
      return res;
    },
  },
  deleteRole: {
    loading: false,
    confirmLoading: false,
    error: {},
    linkedUser: false,
    call: (role: RolesTypes.RoleModel, isLinked?: boolean) => {
      set((state) => {
        if (isLinked === true) {
          state.deleteRole.loading = true;
        } else {
          state.deleteRole.confirmLoading = true;
        }
      });

      return new Promise<RolesTypes.DeleteRoleResponse>((resolve, reject) => {
        BaseService.delete(
          `${environment.api.roles}/${role.roleId}${
            isLinked ? "?isLinked=true" : ""
          }`
        )
          .then((res) => {
            set((state) => {
              state.deleteRole.linkedUser = false;
              state.deleteRole.error = res.data;
            });
            resolve(res.data);
          })
          .catch((error) => {
            set((state) => {
              state.deleteRole.linkedUser = true;
              state.deleteRole.error = error.response;
            });
            reject(error.response);
          })
          .finally(() => {
            set((state) => {
              state.deleteRole.loading = false;
              state.deleteRole.confirmLoading = false;
            });
          });
      });
    },
  },
  refreshRoles: () => {
    set((state) => {
      state.getRoles.fetched = false;
    });
    get().getRoles.call();
  },
  clearRoles: () => {
    set((state) => {
      state.getRoles.fetched = false;
      state.rolePaginationKey = "";
      state.roles = [];
    });
  },
});
